/*
 * Static site copy shared by several pages (process, quality, export, about).
 * Products and categories are NOT here: they live in the database and are edited in the CMS.
 */

import type { IconItem } from '@/types'
import type { LucideIcon } from 'lucide-react'

import {
    Atom,
    Bug,
    Droplet,
    Earth,
    FileCheck,
    FlaskConicalOff,
    Gem,
    Handshake,
    Layers,
    Leaf,
    Microscope,
    Mountain,
    PackageCheck,
    Plane,
    SearchCheck,
    Ship,
    ShieldCheck,
    Sprout,
    TestTubeDiagonal,
    Truck
} from 'lucide-react'
import { MEDIA } from '@/constants/media'

export type ProcessStep = {
    id: string
    number: string
    icon: LucideIcon
    title: string
    summary: string
    details: string[]
    image: {
        src: string
        alt: string
        className: string
    }
}

export type GalleryItem = {
    src: string
    alt: string
    caption: string
    width: number
    height: number
}

export const TRUST_POINTS: IconItem[] = [
    {
        icon: Leaf,
        title: "۱۰۰٪ گیاهی",
        text: "مواد اولیه طبیعی و خالص، بدون هیچ افزودنی"
    },
    {
        icon: FlaskConicalOff,
        title: "بدون افزودنی شیمیایی",
        text: "بدون نگهدارنده، رنگ و اسانس مصنوعی"
    },
    {
        icon: ShieldCheck,
        title: "کنترل کیفیت تخصصی",
        text: "آنالیز آزمایشگاهی هر محموله پیش از بسته‌بندی"
    },
    {
        icon: Earth,
        title: "استاندارد صادراتی",
        text: "قابل اعتماد در بازارهای داخلی و صادراتی"
    }
]

export const PROCESS_STEPS: ProcessStep[] = [
    {
        id: 'harvest',
        number: "۰۱",
        icon: Mountain,
        title: "برداشت تخصصی از کوه",
        summary: "گیاهان در ارتفاعات بکر زاگرس به‌صورت خودرو می‌رویند. برداشت در فصل گلدهی و اوج مواد مؤثره، به‌دست برداشت‌کنندگان بومی و آشنا با کوه انجام می‌شود.",
        details: [
            "شناسایی دقیق گونه پیش از برداشت",
            "چیدن در فصل و ساعت مناسب برای حفظ مواد مؤثره",
            "برداشت گزینشی تا رویشگاه برای سال‌های بعد آسیب نبیند",
            "خشک‌کردن در سایه و جریان هوای طبیعی"
        ],
        image: {
            src: MEDIA.zagrosValley,
            alt: "دره‌ای مه‌آلود در میان کوه‌های سرسبز",
            className: 'object-[50%_72%]'
        }
    },
    {
        id: 'lab',
        number: "۰۲",
        icon: Microscope,
        title: "آنالیز آزمایشگاهی",
        summary: "از هر محموله نمونه‌برداری می‌شود و نمونه‌ها از نظر اصالت گیاه، آلودگی‌ها و میزان مواد مؤثره آزمایش می‌شوند. فقط محموله‌هایی که در محدوده استاندارد باشند به مرحله بعد می‌رسند.",
        details: [
            "شناسایی گیاه‌شناسی و تأیید اصالت گونه",
            "اندازه‌گیری رطوبت، خاکستر و مواد خارجی",
            "آزمون آلودگی میکروبی، فلزات سنگین و باقیمانده سموم",
            "سنجش درصد اسانس و مواد مؤثره"
        ],
        image: {
            src: MEDIA.laboratory,
            alt: "آزمایشگاه کنترل کیفیت خوزکیمیا زاگرس با کارشناسان در حال آنالیز نمونه‌ها",
            className: 'object-[50%_32%]'
        }
    },
    {
        id: 'packaging',
        number: "۰۳",
        icon: PackageCheck,
        title: "بسته‌بندی بهداشتی",
        summary: "محموله‌های تأییدشده در محیطی بهداشتی، در بسته‌های خرده‌فروشی یا فله صادراتی بسته‌بندی می‌شوند و برچسب کامل می‌گیرند تا در تمام مسیر قابل ردیابی باشند.",
        details: [
            "بسته‌بندی در محیط بهداشتی و کنترل‌شده",
            "بسته‌های خرده‌فروشی و فله صادراتی",
            "برچسب شامل نام علمی، شماره بچ و تاریخ تولید و انقضا",
            "محافظت در برابر نور، رطوبت و ضربه در طول حمل"
        ],
        image: {
            src: MEDIA.packagingLine,
            alt: "خط بسته‌بندی محصولات گیاهی خوزکیمیا زاگرس",
            className: 'object-right-top scale-[1.3] origin-top-right'
        }
    },
    {
        id: 'export',
        number: "۰۴",
        icon: Ship,
        title: "ارسال به کشورهای مختلف",
        summary: "مدارک صادراتی هر محموله آماده می‌شود و ارسال هوایی، دریایی یا زمینی تا مقصد خریدار هماهنگ و پیگیری می‌شود.",
        details: [
            "آماده‌سازی گواهی مبدأ، گواهی بهداشت گیاهی و برگه آنالیز",
            "هماهنگی حمل هوایی، دریایی و زمینی",
            "ارسال نمونه برای خریداران عمده",
            "پیگیری محموله تا تحویل به خریدار"
        ],
        image: {
            src: MEDIA.globalExportEn,
            alt: "نقشه صادرات محصولات گیاهی از کوه‌های زاگرس به قاره‌های جهان",
            className: 'object-center'
        }
    }
]

export const LAB_TESTS: IconItem[] = [
    {
        icon: SearchCheck,
        title: "شناسایی گیاه‌شناسی",
        text: "تأیید گونه و اصالت گیاه با بررسی ظاهری و میکروسکوپی"
    },
    {
        icon: Droplet,
        title: "رطوبت",
        text: "رطوبت کنترل‌شده برای جلوگیری از کپک‌زدگی در انبار و حمل"
    },
    {
        icon: Layers,
        title: "خاکستر و مواد خارجی",
        text: "اطمینان از پاکی محصول از خاک، سنگ‌ریزه و ساقه‌های اضافه"
    },
    {
        icon: Bug,
        title: "آلودگی میکروبی",
        text: "شمارش کلی میکروب‌ها، کپک و مخمر و بررسی باکتری‌های بیماری‌زا"
    },
    {
        icon: Atom,
        title: "فلزات سنگین",
        text: "سنجش سرب، کادمیوم، آرسنیک و جیوه در محدوده مجاز"
    },
    {
        icon: FlaskConicalOff,
        title: "باقیمانده آفت‌کش‌ها",
        text: "بررسی باقیمانده سموم کشاورزی در محصول نهایی"
    },
    {
        icon: TestTubeDiagonal,
        title: "اسانس و مواد مؤثره",
        text: "سنجش کیفیت دارویی و عطری هر محموله"
    },
    {
        icon: FileCheck,
        title: "برگه آنالیز هر بچ",
        text: "ارائه نتیجه آنالیز همراه محموله به خریدار"
    }
]

export const EXPORT_REGIONS = [
    { name: "اروپا", latin: 'Europe' },
    { name: "آسیا", latin: 'Asia' },
    { name: "خاورمیانه", latin: 'Middle East' },
    { name: "آفریقا", latin: 'Africa' },
    { name: "آمریکا", latin: 'Americas' },
    { name: "استرالیا", latin: 'Australia' }
]

export const EXPORT_DOCUMENTS = [
    { name: "گواهی مبدأ", latin: 'Certificate of Origin' },
    { name: "گواهی بهداشت گیاهی", latin: 'Phytosanitary Certificate' },
    { name: "برگه آنالیز هر بچ", latin: 'Certificate of Analysis' },
    { name: "فاکتور تجاری و لیست بسته‌بندی", latin: 'Invoice & Packing List' }
]

export const SHIPPING_MODES: IconItem[] = [
    {
        icon: Plane,
        title: "حمل هوایی",
        text: "برای نمونه‌ها، زعفران و محموله‌های کم‌حجم و فوری"
    },
    {
        icon: Ship,
        title: "حمل دریایی",
        text: "برای محموله‌های فله و حجم بالا با هزینه مناسب"
    },
    {
        icon: Truck,
        title: "حمل زمینی",
        text: "برای کشورهای همسایه و بازارهای منطقه"
    }
]

export const ORDER_STEPS = [
    {
        title: "ارسال استعلام",
        text: "محصول، مقدار تقریبی و کشور مقصد را از راه فرم یا واتس‌اپ برای ما بفرستید."
    },
    {
        title: "پیش‌فاکتور و نمونه",
        text: "مشخصات فنی، قیمت و شرایط تحویل را دریافت می‌کنید و در صورت نیاز نمونه ارسال می‌شود."
    },
    {
        title: "آنالیز و بسته‌بندی",
        text: "پس از تأیید سفارش، محموله آنالیز و مطابق سفارش شما بسته‌بندی می‌شود."
    },
    {
        title: "حمل و تحویل",
        text: "مدارک صادراتی آماده می‌شود و محموله تا مقصد ارسال و پیگیری می‌شود."
    }
]

export const EXPORT_FAQS = [
    {
        question: "آیا پیش از سفارش عمده می‌توانم نمونه دریافت کنم؟",
        answer: "بله. برای خریداران عمده، نمونه محصول همراه با مشخصات فنی ارسال می‌شود تا پیش از سفارش، کیفیت را خودتان بررسی کنید."
    },
    {
        question: "برگه آنالیز محصول را دریافت می‌کنم؟",
        answer: "بله. نتیجه آنالیز آزمایشگاهی هر بچ همراه محموله ارائه می‌شود و در صورت درخواست، پیش از ارسال نیز برای شما فرستاده می‌شود."
    },
    {
        question: "حداقل مقدار سفارش چقدر است؟",
        answer: "حداقل سفارش به نوع محصول و روش بسته‌بندی بستگی دارد. محصول و مقدار مورد نظرتان را در فرم استعلام بنویسید تا شرایط دقیق را اعلام کنیم."
    },
    {
        question: "محصولات به چه کشورهایی ارسال می‌شود؟",
        answer: "به بازارهای اروپا، آسیا، خاورمیانه، آفریقا، آمریکا و استرالیا ارسال می‌کنیم و برای هر مقصد، مدارک و روش حمل مناسب همان کشور آماده می‌شود."
    },
    {
        question: "محصولات چگونه بسته‌بندی می‌شوند؟",
        answer: "در بسته‌های خرده‌فروشی یا فله صادراتی، با برچسب کامل شامل نام علمی، شماره بچ و تاریخ تولید و انقضا. اگر بسته‌بندی ویژه‌ای در نظر دارید، جزئیات آن را در استعلام بنویسید."
    },
    {
        question: "شرایط پرداخت و تحویل چگونه است؟",
        answer: "روش پرداخت و شرایط تحویل بر اساس مقصد و حجم سفارش در پیش‌فاکتور توافق می‌شود."
    }
]

export const BRAND_VALUES: IconItem[] = [
    {
        icon: Gem,
        title: "کیفیت",
        text: "هر محموله پیش از بسته‌بندی آنالیز می‌شود؛ کیفیت برای ما اندازه‌گرفتنی است، نه یک ادعا."
    },
    {
        icon: Mountain,
        title: "اصالت",
        text: "ریشه ما در زاگرس، خوزستان و فرهنگ بختیاری است و گیاهانمان از همین سرزمین می‌آیند."
    },
    {
        icon: Handshake,
        title: "اعتماد",
        text: "شفافیت در مشخصات، آنالیز و قیمت؛ چون اعتماد شما سرمایه ماست."
    },
    {
        icon: Sprout,
        title: "برداشت پایدار",
        text: "برداشت گزینشی و چرخشی تا رویشگاه‌های کوه برای نسل‌های بعد باقی بماند."
    }
]

export const BRAND_ROOTS = [
    { title: "خوزستان", text: "سرزمین نخل و رود" },
    { title: "بختیاری", text: "فرهنگ، اصالت، غیرت" },
    { title: "زاگرس", text: "طبیعت بکر، گیاهان کوهی" },
    { title: "ایران", text: "گنجینه گیاهان دارویی" }
]

export const GALLERY_ITEMS: GalleryItem[] = [
    {
        src: MEDIA.brandBoardDark,
        alt: "نشان خوزکیمیا زاگرس با کوه، رود، نخل و برگ طلایی در کنار کاربردهای برند",
        caption: "هویت بصری خوزکیمیا زاگرس",
        width: 1254,
        height: 1254
    },
    {
        src: MEDIA.globeEmblem,
        alt: "نشان طلایی KZ روی کره زمین با کوهستان، گیاهان دارویی و پرچم ایران",
        caption: "نشان KZ، تجارت جهانی گیاهان دارویی",
        width: 843,
        height: 1264
    },
    {
        src: MEDIA.productLineup,
        alt: "مجموعه محصولات خوزکیمیا زاگرس شامل زعفران، دمنوش‌ها و اسانس‌ها",
        caption: "مجموعه محصولات",
        width: 1536,
        height: 1024
    },
    {
        src: MEDIA.attariEmblem,
        alt: "نشان عطاری و داروهای گیاهی با نمادهای خوزستان، بختیاری، ایران و جهان",
        caption: "عطاری و داروهای گیاهی",
        width: 1254,
        height: 1254
    },
    {
        src: MEDIA.globalExportFa,
        alt: "پوستر صادرات جهانی محصولات گیاهی از کوه‌های زاگرس",
        caption: "از دل کوه‌های زاگرس تا هر گوشه از جهان",
        width: 1536,
        height: 1024
    },
    {
        src: MEDIA.office,
        alt: "دفتر خوزکیمیا زاگرس با نشان‌های برند، ظرف‌های زعفران و گیاهان خشک",
        caption: "دفتر و ویترین برند",
        width: 843,
        height: 1264
    },
    {
        src: MEDIA.brandBoardLight,
        alt: "نشان خوزکیمیا زاگرس روی زمینه روشن به همراه تابلو، جعبه و شیشه محصول",
        caption: "نشان و کاربردهای آن",
        width: 1254,
        height: 1254
    },
    {
        src: MEDIA.packagingLine,
        alt: "خط بسته‌بندی محصولات گیاهی و دارویی",
        caption: "تولید و بسته‌بندی استاندارد",
        width: 1536,
        height: 1024
    },
    {
        src: MEDIA.socialPosts,
        alt: "شش طرح تبلیغاتی خوزکیمیا زاگرس برای شبکه‌های اجتماعی",
        caption: "طرح‌های شبکه‌های اجتماعی",
        width: 1536,
        height: 1024
    },
    {
        src: MEDIA.exportContact,
        alt: "پوستر صادرات خوزکیمیا زاگرس همراه با راه‌های ارتباطی",
        caption: "از اصالت بختیاری و ایران، به‌سوی بازارهای جهان",
        width: 1536,
        height: 1024
    }
]
